import { isTouchDevice } from "src/utilities/capabilities"
import { generateUuid } from "src/utilities/uuid"
import UAParser from "ua-parser-js"
import { OnCompleteSuccessResponse } from "webostvjs"

import { dimensions, Platform, platform } from "./constants"
import { captureSentryException } from "./sentry"

/**
 * Converts user agent to a string that can be used by the OAuth server’s device_info field
 * e.g. `Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_4) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/81.0.4044.138 Safari/537.36`
 * will return `Chrome 81 (Mac OS)`
 *
 * @example
 * ```
 * const deviceInfo = getWebDeviceInfo();
 * // deviceInfo = Mozilla Firefox 97 (Mac OS)
 * ```
 */
export function getWebDeviceInfo() {
  const parser = new UAParser()
  const browser = parser.getBrowser()
  const os = parser.getOS()
  const browserName = browser.name
  const majorBrowserVersion = browser.version?.split(".")[0]
  const osName = os.name
  const browserInfo = [browserName, majorBrowserVersion]
    .filter(Boolean)
    .join(" ")

  return `${browserInfo}${osName ? ` (${osName})` : ""}` || "Unknown Device"
}

// Encode the values that are accepted by the RecordPlaybackProgressInput.clientDevice field
// from the web client.
const ClientDevice = {
  WebDesktop: "WebDesktop",
  WebiOS: "WebiOS",
  WebAndroid: "WebAndroid",
  WebTablet: "WebTablet",
  WebOther: "WebOther",
} as const

/**
 * Returns the client device type
 *
 * @example
 * ```
 * const clientDevice = getClientDevice();
 * // clientDevice = `WebDesktop` or `WebiOS` or `WebAndroid` or `WebOther`
 * ```
 */
export function getClientDevice(): keyof typeof ClientDevice {
  const parser = new UAParser()
  const os = parser.getOS()
  const isTouch = isTouchDevice()
  switch (os.name) {
    case "iOS":
      return ClientDevice.WebiOS
    case "Android":
      return ClientDevice.WebAndroid
    case "Mac OS":
      // iPadOs is advertised as MacOS in UserAgent by default
      return isTouch ? ClientDevice.WebiOS : ClientDevice.WebDesktop
    case "Linux":
    case "Windows":
      return ClientDevice.WebDesktop
    default:
      return ClientDevice.WebOther
  }
}

interface DeviceInfoParams {
  keys: string[]
}
interface DeviceInfoData extends OnCompleteSuccessResponse {
  sdkVersion: string
  modelName: string
  firmwareVersion: string
  UHD: string
}
interface DeviceIdParams {
  idType: string[]
}
interface DeviceIdData extends OnCompleteSuccessResponse {
  idList: { idValue: string; idType: string }[]
}

export const generateGenericId = () => {
  const cookieKey = "dgplus_device_id"
  // Get device id from cookie
  const deviceId = window.localStorage.getItem(cookieKey) ?? generateUuid()
  window.localStorage.setItem(cookieKey, deviceId)
  return deviceId
}

export function getClientDeviceId(): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    if (platform === Platform.Web || platform === Platform.Chromecast) {
      resolve(generateGenericId())
    } else if (platform === Platform.WebOS) {
      const webOS = window.webOS
      /**
       * make sure the app doesn't crash on unsupported devices. We aren't certain
       * that this function exists on the client
       */
      if (
        !webOS?.service?.request ||
        typeof webOS?.service?.request !== "function"
      )
        rejectWithSentry(reject, "failed to initialize WebOS API")
      try {
        webOS.service.request<DeviceIdParams, DeviceIdData>(
          "luna://com.webos.service.sm",
          {
            method: "deviceid/getIDs",
            parameters: {
              idType: ["LGUDID"],
            },
            onSuccess: function (inResponse) {
              if (inResponse?.idList && inResponse.idList.length > 0) {
                const idItem =
                  inResponse.idList.find((item) => item.idType === "LGUDID") ??
                  inResponse.idList[0]
                resolve(idItem.idValue)
              } else {
                rejectWithSentry(reject, "failed to retrieve ID")
              }
            },
            onFailure: function () {
              rejectWithSentry(reject, "failed to retrieve ID")
            },
          },
        )
      } catch (_e) {
        rejectWithSentry(reject, "failed to initialize WebOS API")
      }
    } else if (platform === Platform.Tizen) {
      const id = window.localStorage.getItem("duid")
      resolve(id !== null ? id : undefined)
    } else {
      rejectWithSentry(reject, "Unhandled Platform")
    }
  })
}

export type DeviceInfo = {
  modelName?: string
  firmwareVersion?: string
  sdkVersion?: string
}
export const getDeviceOSInfo = async (): Promise<DeviceInfo> => {
  return new Promise(async (resolve, reject) => {
    // For web debugging
    if (platform === Platform.Web) {
      // not interesting enough to provide actual data
      resolve({ firmwareVersion: getWebDeviceInfo() })
    } else if (platform === Platform.Chromecast) {
      resolve({ firmwareVersion: cast.framework.VERSION })
    } else if (platform === Platform.WebOS) {
      const webOS = window.webOS
      /**
       * make sure the app doesn't crash on unsupported devices. We aren't certain
       * that this function exists on the client
       */
      if (
        !webOS?.service?.request ||
        typeof webOS?.service?.request !== "function"
      )
        rejectWithSentry(reject, "failed to initialize WebOS API")
      webOS.service.request<DeviceInfoParams, DeviceInfoData>(
        "luna://com.webos.service.tv.systemproperty",
        {
          method: "getSystemInfo",
          parameters: {
            keys: ["modelName", "firmwareVersion", "sdkVersion"],
          },
          onSuccess: function ({ modelName, firmwareVersion, sdkVersion }) {
            resolve({ sdkVersion, firmwareVersion, modelName })
          },
          onFailure: function () {
            rejectWithSentry(reject, "Error calling LUNA service")
          },
        },
      )
    } else if (platform === Platform.Tizen) {
      const sdkVersion = window.localStorage.getItem("sdkVersion")
      const firmwareVersion = window.localStorage.getItem("firmwareVersion")
      const modelName = window.localStorage.getItem("modelName")
      resolve({
        sdkVersion: sdkVersion !== null ? sdkVersion : undefined,
        firmwareVersion: firmwareVersion !== null ? firmwareVersion : undefined,
        modelName: modelName !== null ? modelName : undefined,
      })
    } else {
      rejectWithSentry(reject, "Unhandled Platform")
    }
  })
}

const rejectWithSentry = (
  reject: (reason?: unknown) => void,
  message: string,
) => {
  captureSentryException(message)
  reject(message)
}

export const getIs4K = async (): Promise<boolean> => {
  return new Promise(async (resolve, reject) => {
    // For web debugging
    if (platform === Platform.Web) resolve(dimensions.height > 720)
    else if (platform === Platform.Chromecast) {
      const context = cast.framework.CastReceiverContext.getInstance()
      resolve(
        context.canDisplayType("video/mp4", "hev1.1.6.L93.B0", 3840, 2160),
      )
    } else if (platform === Platform.WebOS) {
      const webOS = window.webOS
      /**
       * make sure the app doesn't crash on unsupported devices. We aren't certain
       * that this function exists on the client
       */
      if (
        !webOS?.service?.request ||
        typeof webOS?.service?.request !== "function"
      )
        rejectWithSentry(reject, "failed to initialize WebOS API")
      webOS.service.request<
        DeviceInfoParams,
        OnCompleteSuccessResponse & { UHD: string }
      >("luna://com.webos.service.tv.systemproperty", {
        method: "getSystemInfo",
        parameters: {
          keys: ["UHD"],
        },
        onSuccess: function ({ UHD }) {
          resolve(UHD === "true")
        },
        onFailure: function () {
          rejectWithSentry(reject, "Error calling LUNA service")
        },
      })
    } else if (platform === Platform.Tizen) {
      resolve(window.localStorage.getItem("is4K") === "true")
    } else {
      rejectWithSentry(reject, "Unhandled Platform")
    }
  })
}

/**
 * alternative way to check for codec capabilities via the browser is as follows:
 *  const mediaConfig4K = {
 *     type: "media-source",
 *     video: {
 *       contentType: "video/mp4; codecs=hev1.1.6.L93.B0",
 *       width: 3840,
 *       height: 2160,
 *       bitrate: 15000000, // 15Mbps (typical 4K bitrate)
 *       framerate: 30, // Test with 30fps first
 *     }
 *   };
 *   const mediaConfigAtmos = {
 *     type: "media-source",
 *     audio: {
 *       contentType: "audio/mp4; codecs=ec-3",
 *       channels: "6",  // 5.1 as a baseline
 *       bitrate: 640000,
 *       samplerate: 48000
 *     }
 *   };
 *   const result = await navigator.mediaCapabilities.decodingInfo(mediaConfig...);
 */
